import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Search, Download } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtMoney, downloadFile } from "@/lib/api";
import { ENTITY_DEFS, subjectName } from "@/lib/entities";
import { useAuth } from "@/context/AuthContext";
import { useRefData, invalidateRef } from "@/hooks/useRefData";
import { FieldInput, inputClass } from "@/components/FieldInput";
import { StatusBadge, Card } from "@/components/Ui";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const NO_MONEY = new Set(["year", "level", "tax_rate"]);
const TRUST_SCOPED = new Set(["trust_subjects", "assets", "accounting", "deadlines", "fiscal", "diary"]);

export function EntityManager({ entity, trustId, showTrust = false, onRowClick, rowActions, compact = false }) {
  const def = ENTITY_DEFS[entity];
  const { can, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const needTrustField = showTrust && TRUST_SCOPED.has(entity);
  const fields = useMemo(
    () => (needTrustField ? [{ k: "trust_id", l: "Trust", t: "trust", req: entity !== "deadlines" }, ...def.fields] : def.fields),
    [needTrustField, def, entity]
  );
  const refNames = useMemo(() => {
    const n = new Set();
    if (needTrustField || showTrust) n.add("trusts");
    if (fields.some((f) => f.t === "subject")) n.add("subjects");
    if (fields.some((f) => f.t === "account") && can("chart_of_accounts")) n.add("chart_of_accounts");
    return [...n];
  }, [fields, needTrustField, showTrust, can]);
  const refs = useRefData(refNames);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/e/${entity}`, { params: trustId ? { trust_id: trustId } : {} });
      setRows(def.sort ? [...data].sort(def.sort) : data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [entity, trustId, def]);
  useEffect(() => { load(); }, [load]);

  const filtered = q ? rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase())) : rows;
  const columns = showTrust && TRUST_SCOPED.has(entity) ? ["trust_id", ...def.columns] : def.columns;
  const fieldOf = (k) => fields.find((f) => f.k === k) || { k, l: k === "_name" ? "Nominativo" : k };

  const render = (row, k) => {
    const f = fieldOf(k);
    const v = row[k];
    if (k === "_name") return <span className="font-medium text-slate-900">{subjectName(row)}</span>;
    if (f.t === "subject") return subjectName((refs.subjects || []).find((s) => s.id === v));
    if (f.t === "trust") return (refs.trusts || []).find((t) => t.id === v)?.name || "—";
    if (k === "status" || k === "priority") return <StatusBadge value={v} label={f.o?.find((o) => o.value === v)?.label} />;
    if (f.t === "select") return f.o?.find((o) => o.value === v)?.label || v || "—";
    if (f.t === "date") return fmtDate(v);
    if (f.t === "bool") return v ? "Sì" : "No";
    if (f.t === "number") return NO_MONEY.has(k) ? v ?? "—" : fmtMoney(v);
    return v || "—";
  };

  const save = async () => {
    const missing = fields.filter((f) => f.req && (form[f.k] === undefined || form[f.k] === ""));
    if (missing.length) return toast.error(`Compila: ${missing.map((m) => m.l).join(", ")}`);
    setSaving(true);
    try {
      const payload = { ...form };
      if (trustId && TRUST_SCOPED.has(entity)) payload.trust_id = trustId;
      if (form.id) await api.put(`/e/${entity}/${form.id}`, payload);
      else await api.post(`/e/${entity}`, payload);
      toast.success(form.id ? "Modifiche salvate" : `${def.singular} creato`);
      invalidateRef(entity);
      setForm(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/e/${entity}/${toDelete.id}`);
      toast.success(entity === "trusts" ? "Trust archiviato (cancellazione logica)" : "Elemento eliminato");
      invalidateRef(entity);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setToDelete(null);
  };

  const totals = entity === "accounting" && filtered.length > 0 && {
    debit: filtered.reduce((s, r) => s + (Number(r.debit) || 0), 0),
    credit: filtered.reduce((s, r) => s + (Number(r.credit) || 0), 0),
  };
  const showActions = can(entity, "u") || can(entity, "d") || rowActions;

  return (
    <Card className={compact ? "" : "tm-fade"} data-testid={`${entity}-manager`}>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between p-4 border-b border-slate-100">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid={`${entity}-search-input`} className={`${inputClass} pl-9`} placeholder="Cerca…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && !trustId && (
            <Button variant="outline" size="sm" data-testid={`${entity}-export-button`} onClick={() => downloadFile(`/export/${entity}`, `${entity}.csv`).catch((e) => toast.error(errMsg(e)))}>
              <Download className="h-4 w-4 mr-1" /> Esporta CSV
            </Button>
          )}
          {can(entity, "c") && (
            <Button size="sm" data-testid={`${entity}-new-button`} className="bg-[#0A2540] hover:bg-[#031B33] text-white" onClick={() => setForm({ ...(def.defaults || {}) })}>
              <Plus className="h-4 w-4 mr-1" /> Nuovo {def.singular.toLowerCase()}
            </Button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid={`${entity}-table`}>
          <thead className="bg-slate-50/80 border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              {columns.map((c) => <th key={c} className="px-4 py-3 font-semibold whitespace-nowrap">{fieldOf(c).l}</th>)}
              {showActions && <th className="px-4 py-3 text-right font-semibold">Azioni</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-slate-400">Caricamento…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="px-4 py-12 text-center text-slate-400" data-testid={`${entity}-empty`}>Nessun elemento presente</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`${entity}-row-${r.id}`} className={`border-b border-slate-100 hover:bg-slate-50/70 transition-colors ${onRowClick ? "cursor-pointer" : ""}`} onClick={() => onRowClick?.(r)}>
                {columns.map((c) => <td key={c} className="px-4 py-3 text-slate-700 max-w-[280px] truncate">{render(r, c)}</td>)}
                {showActions && (
                  <td className="px-4 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {rowActions?.(r, load)}
                    {can(entity, "u") && (
                      <Button variant="ghost" size="icon" data-testid={`${entity}-edit-${r.id}`} onClick={() => setForm({ ...r })}><Pencil className="h-4 w-4" /></Button>
                    )}
                    {can(entity, "d") && (
                      <Button variant="ghost" size="icon" data-testid={`${entity}-delete-${r.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setToDelete(r)}><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {totals && (
              <tr className="bg-slate-50 font-semibold" data-testid="accounting-totals">
                <td className="px-4 py-3" colSpan={columns.indexOf("debit")}>Totali — Saldo {fmtMoney(totals.debit - totals.credit)}</td>
                <td className="px-4 py-3 font-mono">{fmtMoney(totals.debit)}</td>
                <td className="px-4 py-3 font-mono">{fmtMoney(totals.credit)}</td>
                {showActions && <td />}
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-3xl max-h-[88vh] overflow-y-auto bg-white" data-testid={`${entity}-form-dialog`}>
          <DialogHeader><DialogTitle className="font-heading">{form?.id ? `Modifica ${def.singular.toLowerCase()}` : `Nuovo ${def.singular.toLowerCase()}`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {form && fields.filter((f) => !f.ro).map((f) => (
              <div key={f.k} className={f.t === "textarea" ? "sm:col-span-2" : ""}>
                {f.t !== "bool" && <label className="block text-xs font-semibold text-slate-600 mb-1.5">{f.l}{f.req && <span className="text-red-500"> *</span>}</label>}
                <FieldInput f={f} value={form[f.k]} refs={refs} testId={`${entity}-field-${f.k}`} onChange={(k, v) => setForm((s) => ({ ...s, [k]: v }))} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)} data-testid={`${entity}-form-cancel`}>Annulla</Button>
            <Button disabled={saving} onClick={save} data-testid={`${entity}-form-save`} className="bg-[#0A2540] hover:bg-[#031B33] text-white">{saving ? "Salvataggio…" : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi l'eliminazione?</AlertDialogTitle>
            <AlertDialogDescription>
              {entity === "trusts" ? "Il Trust verrà archiviato con cancellazione logica: i dati restano conservati." : "L'elemento verrà rimosso; una copia resta conservata nell'archivio delle eliminazioni."} L'operazione è registrata nel log di audit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-button" className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmDelete}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
